import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { SupportHubRequest, FeedbackFileInfo, SupportHubTeam, SupportHubRoleLookup, clarificationsEntry, ReleaseVersionStatusModel } from './support-hub.models';
import { MfgappsEventService } from 'src/app/services/mfgapps-event.service';
import { MfgappsApiService } from 'src/app/services/mfgapps-api.service';
import { FileUpload } from 'primeng/fileupload';

class LookupItemModel {
  constructor(
    public id: number,
    public name: string,
    public desc: string
  ) {}
}

class UseCaseWorkCategoryModel {
  constructor(
    public id: number,
    public wcId: number,
    public wcName: string,
    public wctId: number,
    public wctName: string
  ) {}
}

class UseCaseApplicationModel {
  constructor(
    public id: number,
    public appId: number,
    public appName: string,
    public appDesc: string
  ) {}
}

class UseCaseModel {
  constructor(
    public id: number,
    public name: string,
    public desc: string,
    public workcategories: UseCaseWorkCategoryModel[],
    public applications: UseCaseApplicationModel[]
  ) {}
}

class FeedbackLookupModel {
  status: LookupItemModel[] = [];
  rank: LookupItemModel[] = [];
  priority: LookupItemModel[] = [];
  workcategory: LookupItemModel[] = [];
  usecase: UseCaseModel[] = [];
  teams: SupportHubTeam[] = [];
  roles: SupportHubRoleLookup[] = [];
  versionStatus: ReleaseVersionStatusModel[] = [];
}

type FeedbackPageRequest = {
  page: number;
  rowsPerPage: number;
  totalCount: number;
  totalPages: number;
};

type FeedbackSearchRequest = {
  filters: Record<string, number | string | string[]>;
  sortby?: string;
  sortOrder?: 'ASC' | 'DESC';
  page: number;
  rowsPerPage: number;
};

export type SupportHubListFilterState = {
  statusId: number | null;
  requestTypeId: number | null;
  applicationId: number | null;
  versionId: number | null;
  teamId: number | null;
  mineOnly?: boolean;
  creationDate: string[] | null;
  showFilters: boolean;
};

export type SupportHubListSortState = {
  sortBy: string;
  sortOrder: 'ASC' | 'DESC';
};

export type SupportHubListPaginationState = {
  page: number;
  rowsPerPage: number;
};

export type ClarificationListEntry = {
  clarifications: clarificationsEntry[],
  open_count: number
}

@Injectable({ providedIn: 'root' })
export class SupportHubStore {
  private static readonly FEEDBACK_EXPORT_PAGE_SIZE = 1000;
  private static readonly EXPORT_FILE_NAME = 'Support_Hub';
  private static readonly EXPORT_ERROR_MESSAGE = 'Unable to export tickets. Please try again !';
  private static readonly EXPORT_EMPTY_MESSAGE = 'No tickets found for export.';
  private static readonly USE_CASE_EDC = 'EDC';
  private static readonly USE_CASE_GEMS = 'GEMS';

  static readonly ROLE_USER = 'User';
  static readonly ROLE_DEVELOPER = 'Developer';
  static readonly ROLE_ADMIN_SUFFIX = 'Admin';

  private static readonly STATUS_ID_NEW = 1;
  private static readonly STATUS_ID_IN_PROGRESS = 2;
  private static readonly STATUS_ID_NEEDS_CLARIFICATION = 3;
  private static readonly STATUS_ID_UAT_VALIDATING = 4;
  private static readonly STATUS_ID_UAT_COMPLETE = 5;
  private static readonly STATUS_ID_DEPLOYING = 6;
  private static readonly STATUS_ID_STAGING = 7;
  private static readonly STATUS_ID_REVIEW = 8;
  private static readonly STATUS_ID_COMPLETE = 9;
  private static readonly STATUS_ID_DUPLICATE = 10;
  private static readonly STATUS_ID_BACKLOG = 11;

  private readonly feedbackListSubject = new BehaviorSubject<SupportHubRequest[]>([]);
  private readonly supportHubListFilterStateSubject = new BehaviorSubject<SupportHubListFilterState>({
    statusId: null,
    requestTypeId: null,
    applicationId: null,
    versionId: null,
    teamId: null,
    mineOnly: false,
    creationDate: null,
    showFilters: false
  });
  private readonly supportHubListSortStateSubject = new BehaviorSubject<SupportHubListSortState>({
    sortBy: '',
    sortOrder: 'ASC'
  });
  private readonly supportHubListPaginationStateSubject = new BehaviorSubject<SupportHubListPaginationState>({
    page: 1,
    rowsPerPage: 10
  });
  readonly currentUserRoleSubject = new BehaviorSubject<string>(SupportHubStore.ROLE_USER);
  readonly selectedUseCaseIdSubject = new BehaviorSubject<number | null>(null);
  private readonly lookupSubject = new BehaviorSubject(new FeedbackLookupModel());
  statusMap!: Record<string, string>;
  readonly statusIds = {
    NEW: SupportHubStore.STATUS_ID_NEW,
    IN_PROGRESS: SupportHubStore.STATUS_ID_IN_PROGRESS,
    NEEDS_CLARIFICATION: SupportHubStore.STATUS_ID_NEEDS_CLARIFICATION,
    UAT_VALIDATING: SupportHubStore.STATUS_ID_UAT_VALIDATING,
    UAT_COMPLETE: SupportHubStore.STATUS_ID_UAT_COMPLETE,
    DEPLOYING: SupportHubStore.STATUS_ID_DEPLOYING,
    STAGING: SupportHubStore.STATUS_ID_STAGING,
    REVIEW: SupportHubStore.STATUS_ID_REVIEW,
    COMPLETE: SupportHubStore.STATUS_ID_COMPLETE,
    DUPLICATE: SupportHubStore.STATUS_ID_DUPLICATE,
    BACKLOG: SupportHubStore.STATUS_ID_BACKLOG
  };

  readonly currentUser: string;

  constructor(
    public readonly eventService: MfgappsEventService,
    private readonly apiService: MfgappsApiService
  ) {
    this.currentUser = this.eventService.getActiveAccount()?.displayName;

    // Load user role from localStorage, default to USER
    const role = localStorage.getItem('supportHubUserRole') || SupportHubStore.ROLE_USER;
    this.currentUserRoleSubject.next(role);

    // Outside of the dev environment, the "View as" dropdown is hidden, so resolve the
    // real role from the backend instead of relying on localStorage.
    if (!this.eventService.isDevelopmentEnvironment) {
      void this.loadCurrentUserRoleFromServer();
    }
  }

  private lookupRequested = false;
  private lookupLoadingPromise: Promise<void> | null = null;

  private ensureLookupLoaded(): Promise<void> {
    if (this.lookupRequested) {
      return this.lookupLoadingPromise ?? Promise.resolve();
    }

    this.lookupRequested = true;
    this.lookupLoadingPromise = (async () => {
      await this.loadFeedbackLookup();
    })();

    return this.lookupLoadingPromise;
  }

  async loadLookup(): Promise<void> {
    await this.ensureLookupLoaded();
  }

  get lookupValue(): FeedbackLookupModel {
    void this.ensureLookupLoaded();
    return this.lookupSubject.value;
  }

  get selectedUseCaseIdValue(): number | null {
    const selectedId = this.selectedUseCaseIdSubject.value;
    if (selectedId && this.lookupValue.usecase.some(item => item.id === selectedId)) {
      return selectedId;
    }
    return this.getDefaultUseCaseId();
  }

  get selectedUseCaseCodeValue(): string {
    return this.getUseCaseCodeById(this.selectedUseCaseIdValue);
  }

  // Lookup options with ID and name for use in select controls
  get criticalityOptions(): LookupItemModel[] {
    return this.lookupValue.rank;
  }

  get priorityOptions(): LookupItemModel[] {
    return this.lookupValue.priority;
  }

  get statusOptions(): LookupItemModel[] {
    return this.lookupValue.status;
  }

  get workCategoryOptions(): LookupItemModel[] {
    return this.lookupValue.workcategory;
  }

  get useCaseOptions(): UseCaseModel[] {
    return this.lookupValue.usecase;
  }

  get versionStatusOptions(): ReleaseVersionStatusModel[] {
    return this.lookupValue.versionStatus;
  }

  getAssignableVersionStatusIds(): Set<number> {
    const assignableVersionStatus = new Set(['Future', 'Development']);
    return new Set(
      this.versionStatusOptions
        .filter(status => assignableVersionStatus.has(status.name))
        .map(status => status.statusId)
    );
  }

  getCompletedVersionStatusIds(): Set<number> {
    const completedVersionStatus = new Set(['Complete']);
    return new Set(
      this.versionStatusOptions
        .filter(status => completedVersionStatus.has(status.name))
        .map(status => status.statusId)
    );
  }

  getUseCaseNameById(id: number): string {
    return this.lookupValue.usecase.find(item => item.id === id)?.name ?? '';
  }

  getUseCaseCodeById(id: number | null): string {
    if (!id) {
      return '';
    }
    return this.getUseCaseNameById(id).trim().toUpperCase();
  }

  getUseCaseByCode(useCaseCode: string | null | undefined): UseCaseModel | undefined {
    const normalizedCode = useCaseCode?.trim().toUpperCase();
    if (!normalizedCode) {
      return undefined;
    }
    return this.lookupValue.usecase.find(item => item.name.trim().toUpperCase() === normalizedCode);
  }

  getDefaultUseCaseId(): number | null {
    const edcUseCase = this.getUseCaseByCode(SupportHubStore.USE_CASE_EDC);
    if (edcUseCase) {
      return edcUseCase.id;
    }
    return this.lookupValue.usecase[0]?.id ?? null;
  }

  setSelectedUseCaseId(useCaseId: number | null): void {
    const validUseCaseId = useCaseId && this.lookupValue.usecase.some(item => item.id === useCaseId)
      ? useCaseId
      : this.getDefaultUseCaseId();
    if (this.selectedUseCaseIdSubject.value !== validUseCaseId) {
      this.selectedUseCaseIdSubject.next(validUseCaseId);
    }
  }

  setSelectedUseCaseFromQuery(useCaseCode: string | null | undefined): number | null {
    const useCaseFromQuery = this.getUseCaseByCode(useCaseCode);
    const selectedUseCaseId = useCaseFromQuery?.id ?? this.getDefaultUseCaseId();
    this.setSelectedUseCaseId(selectedUseCaseId);
    return selectedUseCaseId;
  }

  getUseCaseQueryParams(additionalParams?: Record<string, string | number | null | undefined>): Record<string, string | number> {
    const params: Record<string, string | number> = {};
    const useCaseCode = this.selectedUseCaseCodeValue;
    if (useCaseCode) {
      params.usecase = useCaseCode;
    }
    if (!additionalParams) {
      return params;
    }
    Object.entries(additionalParams).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        params[key] = value;
      }
    });
    return params;
  }

  setCompanyPlantShopLabels(companyList: any[], request: SupportHubRequest) {
    const company = companyList.find(c => c.company_id === request?.company_id);
    request.company = company?.company_code ?? request.company ?? '';
    const plant = company?.plantList.find((p: any) => p.plant_id === request?.plant_id);
    request.plant = plant?.plant_name ?? request.plant ?? '';
    request.shop = plant?.shopList.find((s: any) => s.shop_id === request?.shop_id)?.shop_name ?? request.shop ?? '';
  }

  // Helper methods to get names by ID
  getCriticalityNameById(id: number | null): string {
    return this.lookupValue.rank.find(item => item.id === id)?.name ?? '';
  }

  getStatusNameById(id: number | null, fallback = ''): string {
    const name = this.lookupValue.status.find(item => item.id === id)?.name;
    return name ?? fallback;
  }

  getWorkCategoryNameById(id: number | null): string {
    return this.lookupValue.workcategory.find(item => item.id === id)?.name ?? '';
  }

  getRequestTypeNameByIds(useCaseId: number, requestTypeId: number | null): string {
    const useCase = this.lookupValue.usecase.find(item => item.id === useCaseId);
    if (!useCase) {
      return '';
    }
    return useCase.workcategories.find(item => item.wctId === requestTypeId)?.wctName ?? '';
  }

  getApplicationNameById(useCaseId: number, applicationId: number | null): string {
    const useCase = this.lookupValue.usecase.find(item => item.id === useCaseId);
    if (!useCase) {
      return '';
    }

    return useCase.applications.find(item => item.appId === applicationId)?.appName ?? '';
  }

  getWorkCategoryOptionsForUseCaseId(useCaseId: number): LookupItemModel[] {
    const useCase = this.lookupValue.usecase.find(item => item.id === useCaseId);
    if (!useCase) {
      return this.workCategoryOptions;
    }

    const unique = new Map<number, LookupItemModel>();
    useCase.workcategories.forEach(item => {
      if (!unique.has(item.wcId)) {
        unique.set(item.wcId, new LookupItemModel(item.wcId, item.wcName, ''));
      }
    });

    return [...unique.values()];
  }

  getPriorityNameById(id?: number): string {
    return this.lookupValue.priority.find(item => item.id === id)?.name ?? '-';
  }

  isStatus(statusOrId: string | number | null, statusId: number): boolean {
    if (typeof statusOrId === 'number') {
      return statusOrId === statusId;
    }
    if (!statusOrId) {
      return false;
    }
    return this.getStatusId(statusOrId) === statusId;
  }

  get statusClassMap(): Record<string, string> {
    if (!this.statusMap) {
      this.statusMap = {};
      this.lookupValue.status.forEach(status => {
        this.statusMap[status.name] = this.getStatusClassById(status.id);
      });
    }
    return this.statusMap;
  }

  getApplicationOptions(useCaseName: string): string[] {
    if (!useCaseName.trim()) {
      return [];
    }
    const useCase = this.lookupValue.usecase.find(item => item.name === useCaseName);
    return (useCase?.applications ?? []).map(item => item.appName);
  }

  getRequestTypeOptions(useCaseName: string, requestCategory: string): string[] {
    if (!useCaseName.trim() || !requestCategory.trim()) {
      return [];
    }
    const normalizedCategoryKey = this.normalizeLookupKey(requestCategory);
    const useCase = this.lookupValue.usecase.find(item => item.name === useCaseName);
    if (!useCase) {
      return [];
    }
    return useCase.workcategories
      .filter(item => this.normalizeLookupKey(item.wcName) === normalizedCategoryKey)
      .map(item => item.wctName);
  }

  getWorkCategoryOptionsForUseCase(useCaseName: string): string[] {
    const useCase = this.lookupValue.usecase.find(item => item.name === useCaseName);
    if (!useCase) {
      return this.workCategoryOptions.map(item => item.name);
    }
    const unique = new Set<string>();
    useCase.workcategories.forEach(item => {
      if (item.wcName) {
        unique.add(item.wcName);
      }
    });
    return [...unique];
  }

  requiresPlantSelection(useCaseName: string): boolean {
    const normalizedUseCase = useCaseName.trim().toUpperCase();
    return normalizedUseCase === SupportHubStore.USE_CASE_EDC || normalizedUseCase === SupportHubStore.USE_CASE_GEMS;
  }

  requiresShopSelection(useCaseName: string): boolean {
    return useCaseName.trim().toUpperCase() === SupportHubStore.USE_CASE_EDC;
  }

  private normalizeLookupKey(value: string): string {
    return value.toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  private async loadFeedbackLookup() {
    try {
      const response = await this.apiService.getFeedbackLookup();
      const lookupData = this.normalizeLookup(response?.result);
      this.lookupSubject.next(lookupData);
      this.setSelectedUseCaseId(this.selectedUseCaseIdSubject.value);
    } 
    catch {
      this.lookupSubject.next(new FeedbackLookupModel());
      this.selectedUseCaseIdSubject.next(null);
    }
  }

  private normalizeLookup(raw: any): FeedbackLookupModel {
    const lookup = new FeedbackLookupModel();
    const items = (list: any[]) => (Array.isArray(list) ? list : []);

    lookup.status = items(raw?.status).map(item => new LookupItemModel(item.id, item.name, item.desc));
    lookup.rank = items(raw?.rank).map(item => new LookupItemModel(item.id, item.name, item.desc));
    lookup.priority = items(raw?.priority).map(item => new LookupItemModel(item.id, item.name, item.desc));
    lookup.workcategory = items(raw?.workcategory).map(item => new LookupItemModel(item.id, item.name, item.desc));
    lookup.usecase = items(raw?.usecase).map((useCase: any) => new UseCaseModel(
      useCase.id,
      useCase.name ?? useCase.Name,
      useCase.desc,
      items(useCase.workcategories).map((wc: any) => new UseCaseWorkCategoryModel(wc.id, wc.wc_id, wc.wc_name, wc.wct_id, wc.wct_name)),
      items(useCase.applications).map((app: any) => new UseCaseApplicationModel(app.id, app.app_id, app.app_name, app.app_desc))
    ));
    lookup.teams = items(raw?.teams).map((t: any): SupportHubTeam => ({
      team_id: t.team_id,
      team_name: t.team_name,
      team_description: t.team_description,
      usecase_id: t.usecase_id ?? null,
      workcategory_id: t.workcategory_id ?? null
    }));
    lookup.roles = items(raw?.roles).map((r: any): SupportHubRoleLookup => ({
      role_id: r.role_id,
      role_name: r.role_name,
      role_description: r.role_description,
      usecase_id: r.usecase_id ?? null,
      team_id: r.team_id ?? null
    }));
    lookup.versionStatus = items(raw?.version_status)
      .map((status: any) => new ReleaseVersionStatusModel(
        status.name,
        status.color_code,
        status.description,
        status.status_id,
        status.sort_order
      ));
    return lookup;
  }

  private getStatusClassById(statusId: number): string {
    switch (statusId) {
      case SupportHubStore.STATUS_ID_NEW:
        return 'status-new';
      case SupportHubStore.STATUS_ID_IN_PROGRESS:
        return 'status-inprogress';
      case SupportHubStore.STATUS_ID_NEEDS_CLARIFICATION:
        return 'status-needsclarification';
      case SupportHubStore.STATUS_ID_UAT_VALIDATING:
        return 'status-pendingvalidation';
      case SupportHubStore.STATUS_ID_DEPLOYING:
      case 6:
        return 'status-deploying';
      case SupportHubStore.STATUS_ID_COMPLETE:
        return 'status-complete';
      case SupportHubStore.STATUS_ID_DUPLICATE:
        return 'status-duplicate';
      default:
        return 'status-other';
    }
  }

  private getStatusId(status: string): number | undefined {
    return this.lookupValue.status.find(item => item.name === status)?.id;
  }

  get feedbackListValue(): SupportHubRequest[] {
    return this.feedbackListSubject.value;
  }

  get supportHubListFilterStateValue(): SupportHubListFilterState {
    return this.supportHubListFilterStateSubject.value;
  }

  get supportHubListSortStateValue(): SupportHubListSortState {
    return this.supportHubListSortStateSubject.value;
  }

  get supportHubListPaginationStateValue(): SupportHubListPaginationState {
    return this.supportHubListPaginationStateSubject.value;
  }

  get currentUserRoleValue(): string {
    return this.currentUserRoleSubject.value;
  }

  isAdminRoleName(roleName: string): boolean {
    return roleName.trim().endsWith(SupportHubStore.ROLE_ADMIN_SUFFIX);
  }

  get uniqueRoleNames(): string[] {
    return [...new Set<string>(this.lookupValue.roles.map(r => r.role_name))];
  }

  setSupportHubListFilterState(state: SupportHubListFilterState): void {
    this.supportHubListFilterStateSubject.next({
      ...state,
      mineOnly: !!state.mineOnly,
      creationDate: state.creationDate ? [...state.creationDate] : null,
      showFilters: !!state.showFilters
    });
  }

  setSupportHubListSortState(state: SupportHubListSortState): void {
    this.supportHubListSortStateSubject.next({ ...state });
  }

  setSupportHubListPaginationState(state: SupportHubListPaginationState): void {
    this.supportHubListPaginationStateSubject.next({
      page: state.page > 0 ? state.page : 1,
      rowsPerPage: state.rowsPerPage > 0 ? state.rowsPerPage : 10
    });
  }

  setCurrentUserRole(role: string) {
    this.currentUserRoleSubject.next(role);
    localStorage.setItem('supportHubUserRole', role);
  }

  /**
   * Resolves the signed-in user's role from the /feedback/users lookup (DB-backed)
   * instead of the client-only dropdown/localStorage value. Used for QA and upper
   * environments where the "View as" role switcher is hidden.
   */
  private async loadCurrentUserRoleFromServer(): Promise<void> {
    try {
      await this.ensureLookupLoaded();
      const currentUserEmail = this.eventService.getActiveAccount()?.emailAddress?.trim().toLowerCase();
      if (!currentUserEmail) {
        return;
      }
      const response = await this.apiService.getFeedbackUsers();
      const matchedUsers = (response?.result ?? []).filter(
        user => user.email_id?.trim().toLowerCase() === currentUserEmail
      );
      if (matchedUsers.length === 0) {
        return;
      }
      const matchedRoleNames = matchedUsers
        .map(user => this.lookupValue.roles.find(role => role.role_id === user.role_id)?.role_name)
        .filter((roleName): roleName is string => !!roleName);
      const resolvedRole = matchedRoleNames.find(roleName => this.isAdminRoleName(roleName))
        ?? matchedRoleNames.find(roleName => roleName === SupportHubStore.ROLE_DEVELOPER)
        ?? SupportHubStore.ROLE_USER;
      this.currentUserRoleSubject.next(resolvedRole);
    }
    catch {
      // Keep the existing/default role if the server lookup fails
    }
  }

  formatUpdatedAt(timestamp: string): string {
    return new Date(`${timestamp}T00:00:00`).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  }

  formatDateString(date: Date): string {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  formatDateTimeString(date: Date): string {
    const datePart = new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
    const timePart = new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }).format(date);
    return `${datePart} · ${timePart}`;
  }

  parseTicketId(ticketId: string): number {
    return Number(ticketId.replace('#', ''));
  }

  findById(feedbackId: number): SupportHubRequest | undefined {
    return this.feedbackListValue.find(request => request.feedback_id === feedbackId);
  }

  /**
   * Assign a ticket to a team member (Triage operation)
   * Only POs can perform this operation
   */
  assignTicket(request: SupportHubRequest, assigneeEmail: string, assigneeName: string) {
    if (request?.status_id !== SupportHubStore.STATUS_ID_NEW) {
      return;
    }
    request.assignedToEmail = assigneeEmail;
    request.assigned_user_name = assigneeName;
    request.status_id = SupportHubStore.STATUS_ID_IN_PROGRESS;
  }

  /**
   * Transition ticket status with optional note and auto-assignment
   */
  async transitionStatus(
    request: SupportHubRequest,
    newStatusId: number,
    option?: number,
    note?: string,
    assignedUserId?: number,
    assignToEmail?: string,
    assignToName?: string) : Promise<boolean | undefined>  {
      
    if (!request) {
      return;
    }

    const payload =  option === 1 ? {
          request_note: note,
          note_to: assignedUserId
      } : {note_from: assignedUserId,
        response_note: note};

    if (newStatusId === SupportHubStore.STATUS_ID_NEEDS_CLARIFICATION || newStatusId === SupportHubStore.STATUS_ID_IN_PROGRESS) {
      try {
        const response = await this.apiService.submitClarificationEntry(Number(request.feedback_id), option!, payload);
        if (response?.message !== 'Success') {
          this.eventService.showSnackBar(response.error ?? `The clarification could not be recorded. Please try again !`, 'error');
          return false;
        }

        request.status_id = newStatusId;
        this.eventService.showSnackBar(`Successfully submitted clarification.`, 'success');
        return true;
      } catch (error: any) {
        this.eventService.showSnackBar((error?.message ?? `The clarification could not be recorded. Please try again !`), 'error');
        return false;
      }      
    }
    else if (newStatusId === SupportHubStore.STATUS_ID_UAT_VALIDATING) {
      request.validationNote = note;
      request.assigned_user = assignedUserId;
      request.assignedToEmail = assignToEmail;
      request.assigned_user_name = assignToName;
    }
  }

  assignValidator(request: SupportHubRequest, validatorEmail: string, validatorName: string) {
    if (!request) {
      return;
    }
    request.validated_user_name = validatorName;
    request.assignedToEmail = validatorEmail;
    request.assigned_user_name = validatorName;
  }

  async toggleTicketLike(request: SupportHubRequest): Promise<boolean> {
    if (!request?.feedback_id) {
      return false;
    }
    const likeMsg = request.is_liked ? 'remove like on' : 'like';
    const postActionMsg = request.is_liked ? 'removed like on' : 'liked';
    try {
      const likes = [...(request.likedByUsers || [])];
      const existingIndex = likes.findIndex(liker => liker.user_name.startsWith(this.currentUser));
      const response = request.is_liked
        ? await this.apiService.unlikeFeedbackEntry(request.feedback_id)
        : await this.apiService.likeFeedbackEntry(request.feedback_id);
      if (response?.message !== 'Success') {
        this.eventService.showSnackBar(response.error ?? `Unable to ${likeMsg} ticket. Please try again !`, 'error');
        return false;
      }
      if (request.is_liked) {
        if (existingIndex >= 0) {
          likes.splice(existingIndex, 1);
        }
      }
      else if (existingIndex < 0) {
        likes.push({ user_id: 0, user_name: this.currentUser });
      }
      request.likedByUsers = likes;
      request.is_liked = !request.is_liked;
      this.eventService.showSnackBar(`Successfully ${postActionMsg} ticket.`, 'success');
      return true;
    }
    catch {
      this.eventService.showSnackBar(`Unable to ${likeMsg} ticket. Please try again !`, 'error');
      return false;
    }
  }

  async toggleTicketFollow(request: SupportHubRequest): Promise<boolean> {
    if (!request?.feedback_id) {
      return false;
    }
    const followMsg = request.is_following ? 'unfollow' : 'follow';
    try {
      const followers = [...(request.followingUsers || [])];
      const existingIndex = followers.findIndex(follower => follower.user_name.startsWith(this.currentUser));
      const response = request.is_following
        ? await this.apiService.unfollowFeedbackEntry(request.feedback_id)
        : await this.apiService.followFeedbackEntry(request.feedback_id);
      if (response?.message !== 'Success') {
        this.eventService.showSnackBar(response.error ?? `Unable to ${followMsg} ticket. Please try again !`, 'error');
        return false;
      }
      if (request.is_following) {
        if (existingIndex >= 0) {
          followers.splice(existingIndex, 1);
        }
      }
      else if (existingIndex < 0) {
        followers.push({ user_id: 0, user_name: this.currentUser });
      }
      request.followingUsers = followers;
      request.is_following = !request.is_following;
      this.eventService.showSnackBar(`Successfully ${followMsg}ed ticket.`, 'success');
      return true;
    }
    catch {
      this.eventService.showSnackBar(`Unable to ${followMsg} ticket. Please try again !`, 'error');
      return false;
    }
  }

  /**
   * Close a ticket as Duplicate or Other (terminal status)
   */
  closeTicket(request: SupportHubRequest, reason: 'Duplicate' | 'Other', _note?: string) {
    if (!request) {
      return;
    }
    if (request.status_id === SupportHubStore.STATUS_ID_DUPLICATE
      || request.status_id === SupportHubStore.STATUS_ID_BACKLOG
      || request.status_id === SupportHubStore.STATUS_ID_COMPLETE) {
      console.info('Ticket is already closed');
      return;
    }
    request.status_id = reason === 'Duplicate'
      ? SupportHubStore.STATUS_ID_DUPLICATE
      : SupportHubStore.STATUS_ID_BACKLOG;
  }

  setTicketStatus(request: SupportHubRequest, newStatusId: number, note?: string) {
    if (!request || request.status_id === newStatusId) {
      return;
    }
    request.status_id = newStatusId;
    if (newStatusId !== SupportHubStore.STATUS_ID_NEEDS_CLARIFICATION) {
      request.clarificationNote = undefined;
    }
    if (newStatusId !== SupportHubStore.STATUS_ID_UAT_VALIDATING) {
      request.validationNote = undefined;
    }
    if (newStatusId === SupportHubStore.STATUS_ID_NEEDS_CLARIFICATION) {
      request.clarificationNote = note || 'Clarification requested';
    }
    if (newStatusId === SupportHubStore.STATUS_ID_UAT_VALIDATING) {
      request.validationNote = note || 'Ready for validation';
    }
  }

  setTicketVersion(request: SupportHubRequest, versionId: number, versionName: string) {
    const normalizedVersionName = versionName.trim();
    if (!request || !normalizedVersionName || (request.version_id === versionId && request.version === normalizedVersionName)) {
      return;
    }
    request.version_id = versionId;
    request.version = normalizedVersionName;
  }

  setTicketCriticality(request: SupportHubRequest, criticalityId: number) {
    if (!request || request.rank_id === criticalityId) {
      return;
    }
    request.rank_id = criticalityId;
  }

  setTicketPriority(request: SupportHubRequest, priorityId: number) {
    if (!request || request.priority_id === priorityId) {
      return;
    }
    request.priority_id = priorityId;
  }

  isTicketSubmitter(request: SupportHubRequest): boolean {
    return request.requested_user_name.startsWith(this.currentUser);
  }

  isAssignedValidator(request: SupportHubRequest): boolean {
    return request.validated_user_name?.startsWith(this.currentUser) ?? false;
  }

  async loadFeedbackPage(page: number, rowsPerPage: number, usecaseId?: number | null): Promise<FeedbackPageRequest> {
    await this.ensureLookupLoaded();
    const effectiveUseCaseId = usecaseId ?? this.selectedUseCaseIdValue;
    return this.loadFeedbackList(page, rowsPerPage, effectiveUseCaseId);
  }

  async searchFeedbackPage(request: FeedbackSearchRequest): Promise<FeedbackPageRequest> {
    await this.ensureLookupLoaded();
    return this.loadFeedbackListBySearch(request);
  }

  async loadClarificationsList(feedbackId: number): Promise<ClarificationListEntry> {
    const response = await this.apiService.getClarificationEntries(feedbackId);
    const isSuccess = response?.message === 'Success';
    return isSuccess ? response?.result ?? { clarifications: [], open_count: 0 } : { clarifications: [], open_count: 0 };
  }

  private async loadFeedbackList(page = 1, rowsPerPage = 10, usecaseId?: number | null): Promise<FeedbackPageRequest> {
    try {
      const response = await this.apiService.getAllFeedbackEntries(page, rowsPerPage, usecaseId ?? 0);
      return this.mapFeedbackListResponse(response, page, rowsPerPage);
    }
    catch {
      this.feedbackListSubject.next([]);
      return {
        page,
        rowsPerPage,
        totalCount: 0,
        totalPages: 1
      };
    }
  }

  private async loadFeedbackListBySearch(request: FeedbackSearchRequest): Promise<FeedbackPageRequest> {
    try {
      const response = await this.apiService.searchFeedbackEntries(request);
      return this.mapFeedbackListResponse(response, request.page, request.rowsPerPage);
    }
    catch {
      this.feedbackListSubject.next([]);
      return {
        page: request.page,
        rowsPerPage: request.rowsPerPage,
        totalCount: 0,
        totalPages: 1
      };
    }
  }

  private mapFeedbackListResponse(response: any, fallbackPage: number, fallbackRowsPerPage: number): FeedbackPageRequest {
    const isSuccess = response?.message === 'Success';
    const records = isSuccess ? response?.result?.records : [];
    const pageMeta: FeedbackPageRequest = {
      page: isSuccess ? Number(response?.result?.page) || fallbackPage : fallbackPage,
      rowsPerPage: isSuccess ? Number(response?.result?.rowsPerPage) || fallbackRowsPerPage : fallbackRowsPerPage,
      totalCount: isSuccess ? Number(response?.result?.totalCount) || 0 : 0,
      totalPages: isSuccess ? Number(response?.result?.totalPages) || 1 : 1
    };
    const parsed = Array.isArray(records)
      ? (records as Array<Partial<SupportHubRequest> & Record<string, any>>)
      : [];
    const value = parsed.map(item => this.normalizeRequest(item));
    this.feedbackListSubject.next(value);
    return pageMeta;
  }

  private normalizeRequest(request: Partial<SupportHubRequest> & Record<string, any>): SupportHubRequest {
    const statusId = request.status_id ?? this.getStatusId(request.status as string) ?? SupportHubStore.STATUS_ID_NEW;
    const rankId = request.rank_id ?? this.lookupValue.rank.find(item => item.name === (request.criticality as string))?.id ?? 0;
    const priorityId = request.priority_id ?? this.lookupValue.priority.find(item => item.name === (request.priority as string))?.id ?? 0;
    const useCaseId = request.usecase_id ?? this.lookupValue.usecase.find(item => item.name === (request.useCase as string))?.id ?? 0;
    const useCase = this.lookupValue.usecase.find(item => item.id === useCaseId);
    const workCategoryId = request.workcategory_id ?? this.lookupValue.workcategory.find(item => item.name === (request.requestCategory as string))?.id ?? 0;
    const requestTypeId = request.workcategorytype_id ?? useCase?.workcategories.find(item => item.wctName === (request.requestType as string))?.wctId ?? 0;
    const applicationId = request.application_id ?? useCase?.applications.find(item => item.appName === (request.application as string))?.appId ?? 0;
    return {
      feedback_id: request.feedback_id ?? 0,
      creation_date: request.creation_date ?? this.formatDateString(new Date()),
      title: request.title ?? '',
      description: request.description ?? '',
      version: request.version_name ?? '',
      corporate_id: request.corporate_id ?? 0,
      company_id: request.company_id ?? 0,
      plant_id: request.plant_id ?? 0,
      shop_id: request.shop_id ?? 0,
      company: request.company ?? '',
      plant: request.plant ?? '',
      shop: request.shop ?? 'N/A',
      status_id: statusId,
      rank_id: rankId,
      priority_id: priorityId,
      usecase_id: useCaseId,
      workcategory_id: workCategoryId,
      workcategorytype_id: requestTypeId,
      application_id: applicationId,
      user_id: request.user_id ?? 0,
      requested_user: request.requested_user,
      requested_user_name: request.requested_user_name ?? '',
      assigned_team_name: request.assigned_team_name,
      assigned_team: request.assigned_team,
      assignedToEmail: request.assignedToEmail,
      assigned_user: request.assigned_user,
      assigned_user_name: request.assigned_user_name,
      contractor_user: request.contractor_user,
      contractor_user_name: request.contractor_user_name,
      validated_user: request.validated_user,
      validated_user_name: request.validated_user_name,
      validationNote: request.validationNote,
      clarificationNote: request.clarificationNote,
      likes_count: request.likes_count ?? 0,
      is_liked: request.is_liked ?? false,
      is_following: request.is_following ?? false,
      public_comments_count: request.public_comments_count ?? 0,
      team_thread_comments_count: request.team_thread_comments_count ?? 0,
      activityLog: request.activityLog ?? []
    };
  }

  // Start: Export Methods
  async exportFeedbackEntries(filters?: Record<string, number | string | string[]>): Promise<void> {
    try {
      const initResponse = await this.apiService.initFeedbackExport({
        page_size: SupportHubStore.FEEDBACK_EXPORT_PAGE_SIZE, filters
      });
      if (initResponse?.message !== 'Success') {
        this.eventService.showSnackBar(SupportHubStore.EXPORT_ERROR_MESSAGE, 'error');
        return;
      }
      const totalPages = Number(initResponse?.result?.total_pages) || 0;
      if (totalPages <= 0) {
        this.eventService.showSnackBar(SupportHubStore.EXPORT_EMPTY_MESSAGE, 'warn');
        return;
      }
      const chunksByPage = await this.fetchExportPageChunks(totalPages, filters);
      if (!chunksByPage) {
        this.eventService.showSnackBar(SupportHubStore.EXPORT_ERROR_MESSAGE, 'error');
        return;
      }
      const exportRows = this.buildExportRows(chunksByPage, totalPages);
      if (exportRows.length < 2) {
        this.eventService.showSnackBar(SupportHubStore.EXPORT_EMPTY_MESSAGE, 'warn');
      }
      else {
        this.eventService.exportDataCSV(exportRows, SupportHubStore.EXPORT_FILE_NAME, false);
      }
    }
    catch {
      this.eventService.showSnackBar(SupportHubStore.EXPORT_ERROR_MESSAGE, 'error');
    }
  }

  private async fetchExportPageChunks(totalPages: number, filters?: Record<string, number | string | string[]>)
    : Promise<Map<number, string> | null> {
    const pageRequests = Array.from({ length: totalPages }, (_, index) => {
      return this.apiService.getFeedbackExportPage({
        page: index + 1,
        page_size: SupportHubStore.FEEDBACK_EXPORT_PAGE_SIZE,
        filters
      });
    });
    const pageResults = await Promise.allSettled(pageRequests);
    const chunksByPage = new Map<number, string>();
    for (const pageResult of pageResults) {
      if (pageResult.status !== 'fulfilled' || pageResult.value?.message !== 'Success') {
        return null;
      }
      const pageNumber = Number(pageResult.value?.result?.page) || 0;
      const csvChunk = pageResult.value?.result?.csv_chunk ?? '';
      if (!pageNumber || !csvChunk) {
        return null;
      }
      chunksByPage.set(pageNumber, csvChunk);
    }
    return chunksByPage;
  }

  private buildExportRows(chunksByPage: Map<number, string>, totalPages: number): Record<string, string>[] {
    const lines: string[] = [];
    for (let page = 1; page <= totalPages; page++) {
      const chunk = chunksByPage.get(page) ?? '';
      chunk.split(/\r\n/).forEach(line => {
        if (line.trim()) {
          lines.push(line);
        }
      });
    }
    const headers = this.parseCsvLine(lines[0]);
    return lines.slice(1).map(line => {
      const parsed = this.parseCsvLine(line);
      const row: Record<string, string> = {};
      headers.forEach((header, index) => {
        row[header] = parsed[index] ?? '';
      });
      return row;
    });
  }

  private parseCsvLine(line: string): string[] {
    const values: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        }
        else {
          inQuotes = !inQuotes;
        }
      }
      else if (char === ',' && !inQuotes) {
        values.push(current.trim());
        current = '';
      }
      else {
        current += char;
      }
    }
    values.push(current.trim());
    return values;
  }

  // Attachment Management Constants
  readonly maxAttachmentCount = 3;
  readonly maxFileSizeMb = 100;

  // Start: Attachment Management Methods
  async handleAttachments(fileUploader: FileUpload, uploadedFiles: File[],
    feedbackFiles: FeedbackFileInfo[], requestId?: number): Promise<void> {
    fileUploader.files = fileUploader.files.filter(f => f.size <= this.maxFileSizeMb * 1024 * 1024);
    const maxAllowedFileCount = this.maxAttachmentCount - feedbackFiles.filter(file => file.feedback_id > 0).length;
    if (fileUploader.files.length > maxAllowedFileCount) {
      this.eventService.showSnackBar(`Maximum attachment count cannot exceed ${this.maxAttachmentCount} !`, 'warn');
      fileUploader.files = fileUploader.files.slice(0, maxAllowedFileCount);
    }
    const newFiles: FeedbackFileInfo[] = [];
    for (const file of uploadedFiles) {
      if ((newFiles.length + feedbackFiles.length) < this.maxAttachmentCount
        && file.size <= this.maxFileSizeMb * 1024 * 1024) {
        newFiles.push({
          feedback_id: requestId ?? 0,
          file_name: file.name,
          file_kb_size: Math.ceil(file.size / 1024),
          file
        });
      }
    }
    feedbackFiles.push(...newFiles);
    if (requestId && newFiles.length > 0 && await this.uploadAttachmentsInParallel(newFiles)) {
      fileUploader.clear();
    }
  }
  
  async uploadAttachmentsInParallel(files: FeedbackFileInfo[], showSnackbar = true): Promise<boolean> {
    const uploadResults = await Promise.allSettled(files.map(file => this.uploadAttachment(file)));
    const hasFailedUploads = uploadResults.some(result => result.status === 'rejected');
    if (hasFailedUploads) {
      const errorMessage = 'One or more attachments failed to upload. Please try again.';
      this.eventService.showSnackBar(errorMessage, 'error');
    }
    else if (files.length > 0 && showSnackbar) {
      this.eventService.showSnackBar(`Uploaded ${files.length} attachment(s) successfully !`, 'success');
    }
    return !hasFailedUploads;
  }

  private async uploadAttachment(fileInfo: FeedbackFileInfo): Promise<void> {
    if (!fileInfo.feedback_id || !fileInfo.file) {
      return;
    }
    const response = await this.apiService.uploadFeedbackFileLarge({
      feedback_id: fileInfo.feedback_id,
      file_name: fileInfo.file_name,
      file_kb_size: fileInfo.file_kb_size
    });
    const uploadUrl: string | undefined = response?.result?.url;
    if (!uploadUrl) {
      throw new Error('Upload URL was not returned by API.');
    }
    await this.apiService.uploadFileOnPresignedUrl(fileInfo.file, uploadUrl);
    fileInfo.id = response?.result?.id;
  }

  async downloadAttachment(fileInfo: FeedbackFileInfo): Promise<void> {
    if (fileInfo?.file) {
      await this.eventService.simulateAnchorClick(fileInfo.file, fileInfo.file_name);
      this.eventService.showSnackBar(`Downloaded attachment '${fileInfo.file_name}' successfully !`, 'success');
    }
    else if (fileInfo.id) {
      const errorMessage = 'Download failed, Please try again !';
      try {
        const response = await this.apiService.downloadFeedbackFile(fileInfo.id);
        const downloadUrl = response?.result?.url;
        if (!downloadUrl) {
          this.eventService.showSnackBar(errorMessage, 'error');
          return;
        }
        const fileResult = await this.apiService.downloadFileFromPresignedUrl(downloadUrl);
        if (fileResult.body instanceof Blob) {
          await this.eventService.simulateAnchorClick(fileResult.body, fileInfo.file_name);
          this.eventService.showSnackBar(`Downloaded attachment '${fileInfo.file_name}' successfully !`, 'success');
        }
        else {
          this.eventService.showSnackBar(errorMessage, 'error');
        }
      }
      catch {
        this.eventService.showSnackBar(errorMessage, 'error');
      }
    }
  }

  async deleteAttachment(fileInfo: FeedbackFileInfo): Promise<boolean> {
    if (!fileInfo) {
      return false;
    }
    if (fileInfo.id) {
      if (!await this.eventService.confirmDialog(
        `Are you sure you want to delete attachment '${fileInfo.file_name}'?`)) {
        return false;
      }
      try {
        await this.apiService.deleteFeedbackFile(fileInfo.id);
        this.eventService.showSnackBar(`Deleted attachment '${fileInfo.file_name}' successfully !`, 'success');
        return true;
      }
      catch {
        this.eventService.showSnackBar('Delete attachment failed, Please try again !', 'error');
        return false;
      }
    }
    else {
      this.eventService.showSnackBar(`Deleted attachment '${fileInfo.file_name}' successfully !`, 'success');
      return true;
    }
  }

  async addSummary(summary: { version_id: number; summary: string[] }): Promise<any> {
    try {
      await this.apiService.addSummary(summary);
       this.eventService.showSnackBar(`Added summary successfully !`, 'success');
    } catch (error) {
      this.eventService.showSnackBar(`Failed to add summary, Please try again !`, 'error');
    }
      
  }
}